import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Table,
    Button,
    Modal,
    Form,
    Image,
    Spinner,
    Alert,
} from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

const BrandsPage = () => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editBrand, setEditBrand] = useState(null);

    // Form state
    const [name, setName] = useState("");
    const [image, setImage] = useState(null);
    const [preview, setPreview] = useState(null);

    const getAuthHeaders = () => ({
        Authorization: localStorage.getItem("authToken")
            ? `Bearer ${localStorage.getItem("authToken")}`
            : "",
    });

    const getImageUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        return `${process.env.REACT_APP_IMG_URL}/${path}`;
    };

    // Fetch Brands
    const fetchBrands = async () => {
        try {
            setLoading(true);
            setError("");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/web-manager/brands`,
                { headers: getAuthHeaders() }
            );
            setBrands(response.data?.data || response.data || []);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load brands.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBrands();
    }, []);

    // Open Modal
    const handleOpenModal = (brand = null) => {
        setErrors({});
        if (brand) {
            setEditBrand(brand);
            setName(brand.name || "");
            setImage(null);
            setPreview(brand.image ? getImageUrl(brand.image) : null);
        } else {
            setEditBrand(null);
            setName("");
            setImage(null);
            setPreview(null);
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditBrand(null);
        setName("");
        setImage(null);
        setPreview(null);
        setErrors({});
    };

    // Handle Image Change
    const handleImageChange = (e) => {
        const file = e.target.files?.[0] || null;
        setImage(file);
        if (file) {
            setPreview(URL.createObjectURL(file));
        }
        setErrors((prev) => ({ ...prev, image: "" }));
    };

    // Create / Update Brand
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErrors({});
        setMessage("");
        setError("");

        try {
            const formData = new FormData();
            formData.append("name", name);

            if (image) {
                formData.append("image", image);
            }

            if (editBrand) {
                // Laravel multipart update requires _method: PUT
                formData.append("_method", "PUT");
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/web-manager/brands/${editBrand.id}`,
                    formData,
                    { headers: getAuthHeaders() }
                );
                setMessage("Brand updated successfully.");
            } else {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/web-manager/brands`,
                    formData,
                    { headers: getAuthHeaders() }
                );
                setMessage("Brand created successfully.");
            }

            handleCloseModal();
            await fetchBrands();
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setError(err.response?.data?.message || "Something went wrong.");
            }
        } finally {
            setSaving(false);
        }
    };

    // Delete Brand
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this brand?")) return;

        try {
            setError("");
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/web-manager/brands/${id}`,
                { headers: getAuthHeaders() }
            );
            setMessage("Brand deleted successfully.");
            await fetchBrands();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to delete brand.");
        }
    };

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">Brands Management</h2>
                        <p className="text-muted mb-0">Manage all website brands and logos.</p>
                    </Col>
                    <Col xs="auto">
                        <Button
                            variant="primary"
                            className="px-4 fw-semibold"
                            onClick={() => handleOpenModal()}
                        >
                            + Add New Brand
                        </Button>
                    </Col>
                </Row>

                {message && (
                    <Alert variant="success" dismissible onClose={() => setMessage("")}>
                        {message}
                    </Alert>
                )}

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" />
                                <div className="mt-2 text-muted">Loading brands...</div>
                            </div>
                        ) : brands.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                No brands found. Click "+ Add New Brand" to create one.
                            </div>
                        ) : (
                            <Table responsive hover className="align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-4">#</th>
                                        <th>Logo</th>
                                        <th>Name</th>
                                        <th className="text-end pe-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {brands.map((brand, index) => (
                                        <tr key={brand.id || index}>
                                            <td className="ps-4 fw-bold text-muted">{index + 1}</td>
                                            <td>
                                                {brand.image ? (
                                                    <Image
                                                        src={getImageUrl(brand.image)}
                                                        alt={brand.name}
                                                        rounded
                                                        style={{
                                                            width: "50px",
                                                            height: "50px",
                                                            objectFit: "contain",
                                                            backgroundColor: "#f8f9fa",
                                                            padding: "4px",
                                                        }}
                                                    />
                                                ) : (
                                                    <span className="badge bg-secondary">No Image</span>
                                                )}
                                            </td>
                                            <td className="fw-semibold">{brand.name}</td>
                                            <td className="text-end pe-4">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleOpenModal(brand)}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(brand.id)}
                                                >
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>

                {/* ADD / EDIT MODAL */}
                <Modal
                    show={showModal}
                    onHide={handleCloseModal}
                    centered
                    backdrop="static"
                    keyboard={false}
                >
                    <Modal.Header closeButton className="border-0 px-4 pt-4 pb-0">
                        <Modal.Title className="fw-bold fs-4 text-dark">
                            {editBrand ? "Edit Brand" : "Add Brand"}
                        </Modal.Title>
                    </Modal.Header>

                    <Form onSubmit={handleSubmit}>
                        <Modal.Body className="px-4 py-3">
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold small text-muted text-uppercase">
                                    Brand Name <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter brand name"
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        setErrors((prev) => ({ ...prev, name: "" }));
                                    }}
                                    isInvalid={!!errors.name}
                                    className="bg-light border-0 py-2 px-3 text-dark"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {Array.isArray(errors.name) ? errors.name[0] : errors.name}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold small text-muted text-uppercase">
                                    Brand Logo / Image
                                </Form.Label>
                                <Form.Control
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.webp"
                                    onChange={handleImageChange}
                                    isInvalid={!!errors.image}
                                    className="bg-light border-0 py-2 px-3 text-dark"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {Array.isArray(errors.image) ? errors.image[0] : errors.image}
                                </Form.Control.Feedback>
                            </Form.Group>

                            {preview && (
                                <div className="text-center p-3 bg-light rounded mt-3 border">
                                    <Image
                                        src={preview}
                                        alt="Preview"
                                        style={{ maxHeight: "80px", objectFit: "contain" }}
                                    />
                                </div>
                            )}
                        </Modal.Body>

                        <Modal.Footer className="border-0 px-4 pb-4 pt-2 d-flex justify-content-end gap-2">
                            <Button
                                variant="secondary"
                                className="px-4 py-2 fw-semibold"
                                onClick={handleCloseModal}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={saving}
                                className="px-4 py-2 fw-semibold text-uppercase"
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : editBrand ? (
                                    "Update Brand"
                                ) : (
                                    "Create Brand"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </Container>
        </PageLayout>
    );
};

export default BrandsPage;